import { createDefaultEmployeeTarget, createEmployeeTargetInstance, EmployeeTarget } from "./employee-target";

export enum PredefinedTimesStatus {
  ENABLED = "ENABLED",
  DISABLED = "DISABLED",
}

export enum PredefinedTimesPeriodMode {
  /** Aucune restriction : la plage s'applique toute l'année, indéfiniment. */
  NONE = "NONE",
  /** Chaque année entre ces dates, jour et mois seulement. Pas d'année associée. */
  RECURRING = "RECURRING",
  /** Bornée à une année précise. Ne se renouvelle pas. */
  LIMITED = "LIMITED",
}

export class PredefinedTimesPeriod {
  mode: PredefinedTimesPeriodMode;
  start: Date;
  end: Date;

  constructor() {
    this.start = new Date();
    this.end = new Date();
    this.mode = PredefinedTimesPeriodMode.NONE;
  }

  public static createInstance(jsonData: PredefinedTimesPeriod): PredefinedTimesPeriod {
    const p = Object.assign(new PredefinedTimesPeriod(), jsonData);

    p.mode = jsonData.mode ?? PredefinedTimesPeriodMode.NONE;
    p.start = jsonData.start ? new Date(jsonData.start) : null;
    p.end = jsonData.end ? new Date(jsonData.end) : null;

    return p;
  }
}

export enum TimeSlotType {
  ACTIVITY = "ACTIVITY",
  MEAL_BREAK = "MEAL_BREAK",
  OTHER_ACTIVITY = "OTHER_ACTIVITY",
}

/** Types de créneau qui comptent dans les heures dues : tout sauf les pauses. */
export const CONTRACT_HOURS_SLOT_TYPES: readonly string[] = [TimeSlotType.ACTIVITY, TimeSlotType.OTHER_ACTIVITY];

export class TimeSlot {
  time_slot_id: string;
  start: Date;
  end: Date;
  type: string;
  meal_break_request: boolean;
  /** Renseigné seulement quand `type` vaut OTHER_ACTIVITY. */
  other_activity_motif_id?: string | null;

  constructor() {
    this.time_slot_id = generateObjectId();
    this.meal_break_request = false;
    this.type = TimeSlotType.ACTIVITY;
    this.start = new Date();
    this.end = new Date();
  }

  public static createInstance(jsonData: TimeSlot): TimeSlot {
    const e = Object.assign(new TimeSlot(), jsonData);
    e.start = jsonData.start ? new Date(jsonData.start) : null;
    e.end = jsonData.end ? new Date(jsonData.end) : null;

    return e;
  }

  public static createInstances(jsonData: TimeSlot[]): TimeSlot[] {
    return jsonData.map((e) => TimeSlot.createInstance(e));
  }
}
export class Times {
  times_id: string;
  time_slots: TimeSlot[] = [];
  days = [];

  constructor() {
    this.times_id = generateObjectId();
    this.time_slots = [new TimeSlot()];
  }

  public static createInstance(jsonData: any): Times {
    const e = Object.assign(new Times(), jsonData);

    if (jsonData.time_slots) {
      e.time_slots = TimeSlot.createInstances(jsonData.time_slots);
    }

    return e;
  }

  public static createInstances(jsonData: Times[]): Times[] {
    return jsonData.map((e) => Times.createInstance(e));
  }
}

export class PlanningPredefinedTimes {
  predefined_times_id: string;
  eva_company_id: string;
  name: string;
  times: Times[] = [];
  period: PredefinedTimesPeriod = new PredefinedTimesPeriod();
  status: PredefinedTimesStatus = PredefinedTimesStatus.ENABLED;
  /** Qui peut utiliser la plage, et — si `defines_contract_hours` — qui en tient ses heures dues. */
  target: EmployeeTarget = createDefaultEmployeeTarget();
  /**
   * Une plage est d'abord un gabarit de préremplissage du planning. Ce drapeau en fait *en plus*
   * l'horaire contractuel des collaborateurs ciblés, sans avoir à le poser fiche par fiche.
   */
  defines_contract_hours = false;
  /**
   * Temps mensuel contractuel, optionnel. Une plage ne décrit qu'une semaine : le facteur de
   * conversion (52/12, 4.33, forfait CCT) vient du contrat. Vide, chaque collaborateur garde le
   * facteur de sa position — approximation qu'il peut corriger sur sa fiche.
   */
  monthly_hours: number = null;

  constructor() {
    this.status = PredefinedTimesStatus.ENABLED;
    this.period = new PredefinedTimesPeriod();
    this.times = [new Times()];
    this.target = createDefaultEmployeeTarget();
  }

  public static createInstance(jsonData: PlanningPredefinedTimes): PlanningPredefinedTimes {
    const c = Object.assign(new PlanningPredefinedTimes(), jsonData);
    if (jsonData.period) {
      c.period = PredefinedTimesPeriod.createInstance(jsonData.period);
    }
    if (jsonData.times) {
      c.times = Times.createInstances(jsonData.times);
    }
    c.target = createEmployeeTargetInstance(jsonData.target);
    c.defines_contract_hours = !!jsonData.defines_contract_hours;
    c.monthly_hours = jsonData.monthly_hours ?? null;

    return c;
  }

  public static createInstances(jsonData: PlanningPredefinedTimes[]): PlanningPredefinedTimes[] {
    return jsonData.map(PlanningPredefinedTimes.createInstance);
  }

  /**
   * Copie une plage vers une nouvelle instance prête à être créée : pas de
   * `predefined_times_id`, ids internes (`times_id`/`time_slot_id`) régénérés par les
   * constructeurs, statut forcé à actif comme toute plage créée. Sert à la fois au
   * brouillon "modifier → créer plutôt" et à l'action "Dupliquer" de la liste.
   */
  public static duplicateFrom(src: PlanningPredefinedTimes): PlanningPredefinedTimes {
    const draft = new PlanningPredefinedTimes();

    draft.name = src.name;
    draft.status = PredefinedTimesStatus.ENABLED;
    draft.target = createEmployeeTargetInstance(src.target);
    draft.defines_contract_hours = src.defines_contract_hours;
    draft.monthly_hours = src.monthly_hours;

    draft.period.mode = src.period.mode;
    draft.period.start = src.period.start ? new Date(src.period.start) : null;
    draft.period.end = src.period.end ? new Date(src.period.end) : null;

    draft.times = src.times.map((time) => {
      const draftTime = new Times();
      draftTime.days = [...time.days];
      draftTime.time_slots = time.time_slots.map((slot) => {
        const draftSlot = new TimeSlot();
        draftSlot.start = slot.start ? new Date(slot.start) : null;
        draftSlot.end = slot.end ? new Date(slot.end) : null;
        draftSlot.type = slot.type;
        draftSlot.meal_break_request = slot.meal_break_request;
        if (slot.other_activity_motif_id) {
          draftSlot.other_activity_motif_id = slot.other_activity_motif_id;
        }

        return draftSlot;
      });

      return draftTime;
    });

    return draft;
  }
}

function generateObjectId(): string {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
}
